/**
 * GlobalModalSlot — 全局浮层统一插槽
 *
 * Architecture 3.0 Phase 4, Layer 4:
 * 从 AppLayout 抽出所有条件渲染的全局模态框 / 全屏路由覆盖层，
 * 统一通过 AgentOSStore 读取开关状态，消除 Prop-Drilling。
 *
 * 包含：LanguageSelector, PrivacyConsent, DocumentGenerator, KnowledgeBase,
 *       PricingPage, SettingsPage, OnboardingGuide, FilePreviewModal, EmailReader
 */

import React from 'react';
import { X } from 'lucide-react';
import { useAgentOSStore } from '../../store/AgentOSStore';
import { ReadingEmailState } from '../../hooks/useEmailProviders';

// Lazy-loaded heavy components
import LanguageSelector from '../LanguageSelector';
import PrivacyConsentDialog from '../PrivacyConsentDialog';
import DocumentGenerator from '../DocumentGenerator';
import KnowledgeBase from '../KnowledgeBase';
import PricingPage from '../PricingPage';
import SettingsPage from '../SettingsPage';
import OnboardingGuide from '../OnboardingGuide';
import FilePreviewModal from '../FilePreviewModal';

// ─── Props ─────────────────────────────────

interface GlobalModalSlotProps {
  currentLanguage: string;
  onLanguageChange: (lang: string) => void;
  isOnSettingsRoute: boolean;
  onSettingsClose: () => void;
  user?: any;
  readingEmail: ReadingEmailState | null;
  setReadingEmail: (email: ReadingEmailState | null) => void;
}

// ─── Component ────────────────────────────

const GlobalModalSlot: React.FC<GlobalModalSlotProps> = ({
  currentLanguage,
  onLanguageChange,
  isOnSettingsRoute,
  onSettingsClose,
  user,
  readingEmail,
  setReadingEmail,
}) => {
  const {
    isLanguageSelectorOpen,
    closeLanguageSelector,
    showPrivacyConsent,
    closePrivacyConsent,
    isDocGeneratorOpen,
    closeDocGenerator,
    isKnowledgeBaseOpen,
    closeKnowledgeBase,
    isPricingOpen,
    closePricing,
    isSettingsOpen,
    closeSettings,
    showOnboarding,
    closeOnboarding,
    previewFile,
    setPreviewFile,
  } = useAgentOSStore();

  return (
    <>
      {/* 语言选择器 */}
      {isLanguageSelectorOpen && (
        <LanguageSelector
          currentLanguage={currentLanguage}
          onLanguageChange={onLanguageChange}
          onClose={closeLanguageSelector}
        />
      )}

      {/* 隐私同意对话框 */}
      {showPrivacyConsent && (
        <PrivacyConsentDialog
          currentLanguage={currentLanguage}
          onAccept={closePrivacyConsent}
        />
      )}

      {/* 法律文书生成器 */}
      <DocumentGenerator
        isOpen={isDocGeneratorOpen}
        onClose={closeDocGenerator}
      />

      {/* 法律知识库 */}
      <KnowledgeBase
        isOpen={isKnowledgeBaseOpen}
        onClose={closeKnowledgeBase}
        userId={user?.id}
      />

      {/* 定价页面 */}
      {isPricingOpen && (
        <PricingPage onClose={closePricing} currentLanguage={currentLanguage} />
      )}

      {/* 设置页面 */}
      <SettingsPage
        isOpen={isSettingsOpen || isOnSettingsRoute}
        onClose={() => {
          closeSettings();
          onSettingsClose();
        }}
        currentLanguage={currentLanguage}
        onLanguageChange={onLanguageChange}
        user={user}
      />

      {/* 新手引导 */}
      {showOnboarding && (
        <OnboardingGuide
          currentLanguage={currentLanguage}
          onClose={closeOnboarding}
        />
      )}

      {/* 文件预览弹窗 */}
      <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />

      {/* 阅读邮件的弹窗 */}
      {readingEmail && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 transition-all" onClick={() => setReadingEmail(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="min-w-0 flex-1 pr-4">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {readingEmail.subject}
                </h3>
                {readingEmail.from && (
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500 truncate">{readingEmail.from}</span>
                    {readingEmail.date && (
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {new Date(readingEmail.date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={() => setReadingEmail(null)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
              >
                <X size={20} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 bg-gray-50/50">
              {readingEmail.isLoading ? (
                <div className="flex flex-col items-center justify-center h-48 space-y-4">
                  <div className="w-8 h-8 rounded-full border-2 border-indigo-200 border-t-indigo-600 animate-spin"></div>
                  <span className="text-gray-500">Loading email content...</span>
                </div>
              ) : readingEmail.error ? (
                <div className="text-red-500 text-center py-8">{readingEmail.error}</div>
              ) : readingEmail.bodyHtml ? (
                <iframe
                  ref={(el) => {
                    if (el && readingEmail.bodyHtml) {
                      const doc = el.contentDocument;
                      if (doc) {
                        const emailStyles = `<style>
                          body {
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                            line-height: 1.6;
                            color: #333;
                            max-width: 100%;
                            margin: 0;
                            padding: 16px;
                            word-wrap: break-word;
                            overflow-wrap: break-word;
                          }
                          a { color: #0066cc; text-decoration: none; }
                          a:hover { text-decoration: underline; }
                          img { max-width: 100%; height: auto; }
                          table { border-collapse: collapse; max-width: 100%; }
                          blockquote { margin: 0; padding-left: 1em; border-left: 3px solid #ddd; }
                          pre { background: #f4f4f4; padding: 10px; border-radius: 4px; overflow-x: auto; }
                        </style>`;
                        doc.open();
                        doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">${emailStyles}</head><body>${readingEmail.bodyHtml}</body></html>`);
                        doc.close();
                        const adjustHeight = () => {
                          if (el && doc.body) {
                            el.style.height = Math.max(doc.body.scrollHeight, 200) + 'px';
                          }
                        };
                        setTimeout(adjustHeight, 100);
                        setTimeout(adjustHeight, 500);
                        setTimeout(adjustHeight, 1500);
                      }
                    }
                  }}
                  className="w-full border-0"
                  style={{ minHeight: '200px' }}
                  sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                  title="Email Content"
                />
              ) : (
                <div className="p-6 text-sm text-gray-700 whitespace-pre-wrap break-words" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
                  {readingEmail.content || ''}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalModalSlot;
